import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart, Users, ShieldCheck, Stethoscope } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { stats, heroImages, workCategories, campaignHighlights } from '../mock';
import HeroCarousel from '../components/HeroCarousel';
import DonorTestimonials from '../components/DonorTestimonials';
import FeaturedStories from '../components/FeaturedStories';
import NewsletterPopup from '../components/NewsletterPopup';
import CampaignHighlights from '../components/CampaignHighlights';
import contentData from '../data/content.json';

const Home = () => {
  const featuredStory = contentData.featuredStories[0];
  const [heroSlides, setHeroSlides] = useState([]);
  const [heroLoading, setHeroLoading] = useState(true);
  const [heroError, setHeroError] = useState(null);
  const [animatedStats, setAnimatedStats] = useState({
    soulsTouched: 0,
    animalsRescued: 0,
    antiRabiesVaccination: 0,
    waterBowls: 0,
    volunteersActive: 0,
    adoptionsDone: 0
  });

  useEffect(() => {
    const fetchHeroSlides = async () => {
      try {
        setHeroLoading(true);
        const response = await fetch('/data/hero-carousel.json');
        if (!response.ok) {
          throw new Error('Failed to fetch hero carousel');
        }
        const data = await response.json();
        setHeroSlides(data);
        setHeroError(null);
      } catch (err) {
        console.error('Error fetching hero carousel:', err);
        setHeroError(err.message);
      } finally {
        setHeroLoading(false);
      }
    };

    fetchHeroSlides();
  }, []);

  useEffect(() => {
    const duration = 2000; // 2 seconds
    const steps = 60; // 60 frames
    const interval = duration / steps;

    let currentStep = 0;
    const timer = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;

      setAnimatedStats({
        soulsTouched: Math.floor(stats.soulsTouched * progress),
        animalsRescued: Math.floor(stats.animalsRescued * progress),
        antiRabiesVaccination: Math.floor(stats.antiRabiesVaccination * progress),
        waterBowls: Math.floor(stats.waterBowls * progress),
        volunteersActive: Math.floor(stats.volunteersActive * progress),
        adoptionsDone: Math.floor(stats.adoptionsDone * progress)
      });

      if (currentStep >= steps) {
        clearInterval(timer);
        setAnimatedStats(stats);
      }
    }, interval);

    return () => clearInterval(timer);
  }, []);

  return (
    <div className="min-h-screen">
      {/* Newsletter Popup */}
      <NewsletterPopup />
      
      {/* Hero Section */}
      <HeroCarousel slides={heroSlides} loading={heroLoading} error={heroError} />

      {/* Stats Section */}
      <section className="py-12 md:py-16 bg-sand-beige">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6">
            <div className="text-center">
              <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-warm-amber font-heading mb-1 md:mb-2">
                {animatedStats.soulsTouched.toLocaleString()}+
              </div>
              <p className="text-xs sm:text-sm md:text-base text-gray-700 font-medium">Souls Touched</p>
            </div>
            <div className="text-center">
              <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-warm-amber font-heading mb-1 md:mb-2">
                {animatedStats.animalsRescued.toLocaleString()}+
              </div>
              <p className="text-xs sm:text-sm md:text-base text-gray-700 font-medium">Animals Rescued</p>
            </div>
            <div className="text-center">
              <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-warm-amber font-heading mb-1 md:mb-2">
                {animatedStats.antiRabiesVaccination.toLocaleString()}+
              </div>
              <p className="text-xs sm:text-sm md:text-base text-gray-700 font-medium">Anti Rabies Vaccination</p>
            </div>
            <div className="text-center">
              <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-warm-amber font-heading mb-1 md:mb-2">
                {animatedStats.waterBowls.toLocaleString()}+
              </div>
              <p className="text-xs sm:text-sm md:text-base text-gray-700 font-medium">Water Bowls</p>
            </div>
            <div className="text-center">
              <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-warm-amber font-heading mb-1 md:mb-2">
                {animatedStats.volunteersActive.toLocaleString()}+
              </div>
              <p className="text-xs sm:text-sm md:text-base text-gray-700 font-medium">Volunteers</p>
            </div>
            <div className="text-center">
              <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-warm-amber font-heading mb-1 md:mb-2">
                {animatedStats.adoptionsDone.toLocaleString()}+
              </div>
              <p className="text-xs sm:text-sm md:text-base text-gray-700 font-medium">Adoptions Done</p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Rescue Story */}
      <section className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
              <div className="relative h-[280px] md:h-[400px] rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={featuredStory.image[0]}
                  alt={featuredStory.title}
                  className="w-full h-full object-contain"
                />
              </div>
              <div>
                <span className="text-warm-amber font-semibold text-xs md:text-sm uppercase tracking-wide">Featured Story</span>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold font-heading text-forest-green mt-2 mb-3 md:mb-4">
                  {featuredStory.title}
                </h2>
                <p className="text-gray-700 text-sm md:text-lg leading-relaxed mb-4 md:mb-6">
                  {
                    (() => {
                      const words = featuredStory.description.split(' ');
                      if (words.length > 150) {
                        return words.slice(0, 150).join(' ') + '...';
                      }
                      return featuredStory.description;
                    })()
                  }
                </p>
                <Link to="/blog">
                  <Button className="bg-warm-amber hover:bg-amber-600 text-white transition-colors duration-200">
                    Read More Stories <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Campaigns */}
      <section className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 md:mb-16">
            <span className="text-warm-amber font-semibold text-xs md:text-sm uppercase tracking-wide">Our Campaigns</span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold font-heading text-forest-green mb-2 md:mb-4 mt-2">
              Driving Change, One Campaign at a Time
            </h2>
            <p className="text-base md:text-xl text-gray-700 max-w-3xl mx-auto px-2">
              From a single water bowl to a full shelter hospital — these are the movements powering our mission.
            </p>
          </div>
          <CampaignHighlights campaigns={campaignHighlights} />
        </div>
      </section>

      {/* What We Do */}
      <section className="py-12 md:py-20 bg-gradient-to-b from-sand-beige to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold font-heading text-forest-green mb-2 md:mb-4">
              What We Do
            </h2>
            <p className="text-base md:text-xl text-gray-700 max-w-3xl mx-auto px-2">
              Our comprehensive approach to animal welfare covers every aspect of rescue, treatment, and rehabilitation.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8 max-w-7xl mx-auto">
            {workCategories.map((category) => (
              <Card key={category.id} className="border-none shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden group">
                <div className="relative h-36 md:h-48 overflow-hidden">
                  <img
                    src={category.image}
                    alt={category.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-forest-green/80 to-transparent"></div>
                </div>
                <CardContent className="p-4 md:p-6">
                  <h3 className="text-base md:text-xl font-bold font-heading text-forest-green mb-2 md:mb-3">{category.title}</h3>
                  <p className="text-gray-700 text-sm md:text-base mb-3 md:mb-4 leading-relaxed line-clamp-2">{category.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs md:text-sm font-semibold text-warm-amber">{category.stats}</span>
                    <ArrowRight className="w-4 h-4 md:w-5 md:h-5 text-warm-amber group-hover:translate-x-1 transition-transform duration-200" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/our-work">
              <Button size="lg" variant="outline" className="border-2 border-forest-green text-forest-green hover:bg-forest-green hover:text-white transition-colors duration-200">
                Learn More About Our Work
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Donor Testimonials */}
      <section className="py-12 md:py-20 bg-gradient-to-b from-white to-sand-beige">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold font-heading text-forest-green mb-2 md:mb-4">
              Stories from Our Supporters
            </h2>
            <p className="text-base md:text-xl text-gray-700 max-w-3xl mx-auto">
              Hear from compassionate donors who make our mission possible
            </p>
          </div>
          <DonorTestimonials />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-20 bg-[#ffffff] text-black">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold font-heading mb-4 md:mb-6">
            Every Animal Deserves a Second Chance
          </h2>
          <p className="text-base md:text-xl mb-6 md:mb-8 max-w-3xl mx-auto px-2">
            Your support makes the difference between life and death. Join us in creating a world where every street animal receives the care they deserve.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center px-4">
            <Link to="/donate" target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="w-full sm:w-auto text-black border border-gray-300 bg-warm-amber hover:bg-warm-amber transition-all duration-300 hover:scale-105 text-base md:text-lg px-6 md:px-8 py-4 md:py-6">
                <Heart className="mr-2 w-4 h-4 md:w-5 md:h-5" fill="currentColor" />
                Make a Donation
              </Button>
            </Link>
            <Link to="/volunteer">
              <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-gray-300 text-warm-amber hover:bg-sand-beige transition-all duration-300 hover:scale-105 text-base md:text-lg px-6 md:px-8 py-4 md:py-6">
                <Users className="mr-2 w-4 h-4 md:w-5 md:h-5" />
                Become a Volunteer
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;